import Button from 'ext-ui/components/Button/Button'
import ButtonGroup from 'ext-ui/components/ButtonGroup/ButtonGroup'
import Dropdown from 'ext-ui/components/Dropdown/Dropdown'
import DropdownButton from 'ext-ui/components/DropdownButton/DropdownButton'
import Modal from 'ext-ui/components/Modal/Modal'
import Overlay from 'ext-ui/components/Overlay/Overlay'
import { useResolvedDesignSystemComponent } from 'ext-ui/design-system/DesignSystemChannel'
import React, { useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { createSetIsToastOpen } from 'src/app/Toast/redux/toast.actions'
import SvgMoreIcon from 'src/components/Svgs/SvgMoreIcon'
import SvgTrash from 'src/components/Svgs/SvgTrash'
import { useTranslate } from 'src/localization/Trns'
import { useArchiveVendor } from 'src/queries/vendors/vendors.queries'
import { Vendor } from 'src/queries/vendors/vendors.types'

import {
  footerError,
  vendorActionMenu,
  vendorActionTrigger,
  VendorArchiveMenuOption,
  visuallyHidden,
} from './VendorFlyout.style'

interface VendorArchiveActionProps {
  vendor: Vendor
  organizationId: string
  onClose: () => void
}

interface MenuSurfaceProps {
  children: React.ReactNode
}

function LiveMenuSurface({ children }: MenuSurfaceProps): React.ReactElement {
  return <Overlay padding={'none'}>{children}</Overlay>
}

function LatestMenuSurface({ children }: MenuSurfaceProps): React.ReactElement {
  return <>{children}</>
}

/**
 * Archive action shared by the edit and Action Required resume views. The
 * backend owns archive eligibility and payment-state side effects; this
 * component only confirms the archive request and refreshes through the
 * mutation hook.
 */
const VendorArchiveAction: React.FC<VendorArchiveActionProps> = ({
  vendor,
  organizationId,
  onClose,
}) => {
  const translate = useTranslate()
  const dispatch = useDispatch()
  const actionMenuRef = useRef<HTMLSpanElement>(null)
  const [menuOpen, setMenuOpen] = useState(false)
  const [confirmationOpen, setConfirmationOpen] = useState(false)
  const [hasError, setHasError] = useState(false)
  const mutation = useArchiveVendor(organizationId)
  const actionLabel = translate('Archive Vendor')
  const MenuSurface = useResolvedDesignSystemComponent(
    LiveMenuSurface,
    LatestMenuSurface,
  )

  const closeConfirmation = (): void => {
    if (mutation.isPending) return
    setHasError(false)
    setConfirmationOpen(false)
  }

  const confirm = (): void => {
    setHasError(false)
    mutation.mutate(
      { vendorId: vendor.id },
      {
        onSuccess: () => {
          setConfirmationOpen(false)
          dispatch(
            createSetIsToastOpen(true, {
              toastMessage: (
                <span>
                  <strong>{translate('Vendor archived')}</strong>{' '}
                  {translate('${vendorName} was archived.', {
                    vendorName: vendor.name,
                  })}
                </span>
              ),
            }),
          )
          onClose()
        },
        onError: () => setHasError(true),
      },
    )
  }

  return (
    <>
      <span css={vendorActionMenu} ref={actionMenuRef}>
        <DropdownButton
          closeOnSelect={false}
          isOpen={menuOpen}
          onToggle={setMenuOpen}
          placement={'bottom-end'}
          trigger={
            <Button
              css={vendorActionTrigger}
              variant={'no-style'}
              aria-label={translate('Vendor actions')}
            >
              <SvgMoreIcon fill={'currentColor'} />
            </Button>
          }
          menu={
            <MenuSurface>
              <Dropdown
                isFooterAbsolute={false}
                aria-label={translate('Vendor actions')}
              >
                <VendorArchiveMenuOption
                  title={actionLabel}
                  variant={'severe'}
                  icon={
                    <SvgTrash
                      fill={'currentColor'}
                      width={16}
                      height={16}
                      viewBox={'0 0 24 24'}
                    />
                  }
                  onClick={() => {
                    setHasError(false)
                    setMenuOpen(false)
                    // Move focus off the menu item before it unmounts so the
                    // modal can restore focus to the action trigger on close.
                    actionMenuRef.current
                      ?.querySelector<HTMLButtonElement>('button')
                      ?.focus()
                    setConfirmationOpen(true)
                  }}
                />
              </Dropdown>
            </MenuSurface>
          }
        />
      </span>

      <Modal
        open={confirmationOpen}
        closable={!mutation.isPending}
        onClose={closeConfirmation}
        title={translate('Archive this vendor?')}
        description={
          <>
            {translate(
              "Once archived, ${vendorName} can't be selected to pay a bill. Any payments to them still waiting for verification will be canceled. Their record and payment history stay intact.",
              { vendorName: vendor.name },
            )}
            {mutation.isPending && (
              <span
                css={visuallyHidden}
                role={'status'}
                aria-live={'polite'}
                aria-atomic={'true'}
              >
                {translate('Archiving vendor…')}
              </span>
            )}
          </>
        }
        footer={
          <ButtonGroup>
            <Button
              variant={'no-style'}
              disabled={mutation.isPending}
              onClick={closeConfirmation}
            >
              {translate('Cancel')}
            </Button>
            <Button
              variant={'severe'}
              loading={mutation.isPending}
              aria-busy={mutation.isPending}
              onClick={confirm}
            >
              {actionLabel}
            </Button>
          </ButtonGroup>
        }
      >
        {hasError && (
          <p css={footerError} role={'alert'}>
            {translate('We could not archive this vendor.')}
          </p>
        )}
      </Modal>
    </>
  )
}

export default VendorArchiveAction
